import React from 'react';
import { Printer } from 'lucide-react';

/**
 * DocumentHeader  --  the editorial-style header that appears at the top of
 * every reading, lesson, and self-work page.  Designed to match the layout
 * of the printable ARP PDFs:
 *
 *   - Big serif title (Lora 700, ~3.5rem)
 *   - Italic serif subtitle
 *   - Small grey eyebrow line ("Programme  ·  Week 3 Self-Work")
 *   - Optional italic intro callout in a subtle grey panel
 *   - Optional "Print this document" button (top-right) that triggers
 *     window.print() with the document-only print stylesheet (defined in
 *     App.css under @media print).
 *
 * Use it INSIDE a white .arp-document wrapper for the serif body styling.
 */
export default function DocumentHeader({
  title,
  subtitle,
  eyebrow,
  intro,
  testIdPrefix = 'doc',
  showPrint = true,
}) {
  const handlePrint = () => {
    // Mark <html> with a class so the print stylesheet can hide every
    // element that doesn't belong to the document being printed.
    document.documentElement.classList.add('arp-printing');
    // Use rAF so the class settles before the print preview renders.
    requestAnimationFrame(() => {
      window.print();
      // Remove the class after the print dialog closes (best-effort).
      setTimeout(() => {
        document.documentElement.classList.remove('arp-printing');
      }, 500);
    });
  };

  return (
    <header className="arp-document-header" data-testid={`${testIdPrefix}-header`}>
      {showPrint && (
        <button
          type="button"
          onClick={handlePrint}
          className="arp-print-btn no-print"
          data-testid={`${testIdPrefix}-print-btn`}
          aria-label="Print this document"
          title="Print this document"
        >
          <Printer className="w-4 h-4" />
          <span>Print this document</span>
        </button>
      )}
      <h1
        className="arp-document-title"
        data-testid={`${testIdPrefix}-title`}
      >
        {title}
      </h1>
      {subtitle && (
        <p
          className="arp-document-subtitle"
          data-testid={`${testIdPrefix}-subtitle`}
        >
          {subtitle}
        </p>
      )}
      {eyebrow && (
        <p
          className="arp-document-eyebrow"
          data-testid={`${testIdPrefix}-eyebrow`}
        >
          {eyebrow}
        </p>
      )}
      {intro && (
        <div
          className="arp-document-intro"
          data-testid={`${testIdPrefix}-intro`}
        >
          {intro}
        </div>
      )}
    </header>
  );
}
